import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { formatDate, formatPeriod, toDateInput } from '../../kit/helpers'
import ActionDialog, { RecordSummary, useActionSubmit } from './ActionDialog'
import {
  METRIC_LABELS,
  MIN_REASON_LENGTH,
  QUOTE_METRICS,
  isDateInput,
  isHttpUrl,
  londonDateInput,
  toNumber,
  todayInput,
} from './constants'

// The per-month action dialogs of the Commercial usage tab. Each one RECORDS
// something a member of staff did by hand (sent an email, issued a quote, made
// a decision) - none of them sends anything or changes a subscription, and the
// acting user is never sent: the server takes it from the CRM session.

const DateInput = ({ label, value, onChange, error, helperText, max, min, required }) => (
  <TextField
    type="date"
    size="small"
    fullWidth
    required={required}
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    error={!!error}
    helperText={error || helperText}
    InputLabelProps={{ shrink: true }}
    inputProps={{ max, min }}
  />
)

// The earliest day an email about a month can have been sent: the day the
// month ended (the record's own periodEnd, as a London date). '' when the
// record's bounds are not known.
const earliestDayFor = (target) => londonDateInput(target?.periodEnd)

// A "when did you do it" date: valid, not in the future and - for an email
// about a month - not before that month had ended.
const pastDateProblem = (value, earliest) => {
  if (!isDateInput(value)) return 'Enter a valid date.'
  if (value > todayInput()) return 'That date is in the future.'
  if (earliest && value < earliest) {
    return `That is before the month had ended - the earliest possible date is ${formatDate(earliest)}.`
  }
  return ''
}

const reasonProblem = (value, what) =>
  value.trim().length < MIN_REASON_LENGTH
    ? `${what} (at least ${MIN_REASON_LENGTH} characters).`
    : ''

// First occurrence: record when the advisory email was sent.
export const AdvisorySentDialog = ({ open, target, onClose, onSubmit }) => {
  const [sentAt, setSentAt] = useState(todayInput())
  const [note, setNote] = useState('')
  const [attempted, setAttempted] = useState(false)
  const { busy, error, run } = useActionSubmit({ open, onSubmit, onClose })

  useEffect(() => {
    if (open) {
      setSentAt(todayInput())
      setNote('')
      setAttempted(false)
    }
  }, [open])

  const earliest = earliestDayFor(target)
  const sentAtProblem = pastDateProblem(sentAt, earliest)
  const confirm = () => {
    setAttempted(true)
    if (sentAtProblem) return
    run({ sentAt, ...(note.trim() ? { note: note.trim() } : {}) })
  }

  return (
    <ActionDialog
      open={open}
      title="Mark advisory sent"
      confirmLabel="Mark advisory sent"
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={confirm}
    >
      <RecordSummary target={target} />
      <Typography variant="body2" color="text.secondary">
        This records that you sent the advisory email from your own mailbox. AmbuNet does not
        send it, and nothing changes on the customer&apos;s account.
      </Typography>
      <DateInput
        label="Date sent"
        required
        value={sentAt}
        onChange={setSentAt}
        min={earliest || undefined}
        max={todayInput()}
        error={attempted && sentAtProblem}
      />
      <TextField
        label="Note (optional)"
        size="small"
        fullWidth
        multiline
        minRows={2}
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
    </ActionDialog>
  )
}

const blankQuote = (currency) => ({
  quoteReference: '',
  quotePackage: '',
  ptsJourneysPerMonth: '',
  controlRoomHoursPerMonth: '',
  concurrentControlRooms: '',
  quoteAmount: '',
  quoteCurrency: currency || 'GBP',
  quoteValidUntil: '',
  quoteLink: '',
  sentAt: todayInput(),
  note: '',
})

// What the quote form holds, as the draft preview query: only the details
// typed so far (the server leaves the rest as [placeholders]).
const quotePreviewQuery = (form) => {
  const query = {}
  for (const key of ['quoteReference', 'quotePackage', 'quoteCurrency', 'quoteValidUntil', 'quoteLink']) {
    if (String(form[key] ?? '').trim()) query[key] = String(form[key]).trim()
  }
  if (toNumber(form.quoteAmount) !== null) query.quoteAmount = toNumber(form.quoteAmount)
  for (const metric of QUOTE_METRICS) {
    if (toNumber(form[metric]) !== null) query[metric] = toNumber(form[metric])
  }
  return query
}

// Second occurrence: record the Enterprise+ quotation that was sent. At a
// third occurrence (`upgradeRequired`) the same form records a REVISED quote,
// which leaves the upgrade required. `previousQuote` pre-fills the form with
// the quote already on record so it can be reviewed rather than retyped.
export const QuoteSentDialog = ({
  open,
  target,
  currency,
  previousQuote,
  onClose,
  onSubmit,
  onPreviewDraft,
}) => {
  const [form, setForm] = useState(() => blankQuote(currency))
  const [attempted, setAttempted] = useState(false)
  const { busy, error, run } = useActionSubmit({ open, onSubmit, onClose })
  const revised = target?.status === 'upgradeRequired' || target?.action?.status === 'upgradeRequired'

  useEffect(() => {
    if (!open) return
    setAttempted(false)
    setForm({
      ...blankQuote(currency),
      ...(previousQuote
        ? {
            quoteReference: previousQuote.quoteReference ?? '',
            quotePackage: previousQuote.quotePackage ?? '',
            ptsJourneysPerMonth: previousQuote.quoteAllowance?.ptsJourneysPerMonth ?? '',
            controlRoomHoursPerMonth: previousQuote.quoteAllowance?.controlRoomHoursPerMonth ?? '',
            concurrentControlRooms: previousQuote.quoteAllowance?.concurrentControlRooms ?? '',
            quoteAmount: previousQuote.quoteAmount ?? '',
            quoteCurrency: previousQuote.quoteCurrency || currency || 'GBP',
            quoteValidUntil: toDateInput(previousQuote.quoteValidUntil),
            quoteLink: previousQuote.quoteLink ?? '',
          }
        : {}),
    })
  }, [open])

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

  // The server's rules (recordQuoteSent), checked before anything is sent.
  const earliest = earliestDayFor(target)
  const allowance = Object.fromEntries(
    QUOTE_METRICS.filter((metric) => String(form[metric]).trim() !== '').map((metric) => [
      metric,
      toNumber(form[metric]),
    ]),
  )
  const amount = toNumber(form.quoteAmount)
  const problems = {
    quoteReference: form.quoteReference.trim() ? '' : 'Enter the quote reference.',
    quotePackage: form.quotePackage.trim() ? '' : 'Enter the proposed Enterprise+ package.',
    quoteAllowance: Object.values(allowance).some((value) => value === null || value <= 0)
      ? 'Each proposed allowance must be a number above zero.'
      : Object.keys(allowance).length === 0
        ? 'Enter at least one proposed allowance.'
        : '',
    quoteAmount: amount !== null && amount > 0 ? '' : 'Enter the quoted fee as a number above zero.',
    quoteCurrency:
      !form.quoteCurrency.trim() || /^[A-Za-z]{3}$/.test(form.quoteCurrency.trim())
        ? ''
        : 'Use a three-letter code such as GBP.',
    sentAt: pastDateProblem(form.sentAt, earliest),
    quoteValidUntil: !isDateInput(form.quoteValidUntil)
      ? 'Enter the date the quote is valid until.'
      : isDateInput(form.sentAt) && form.quoteValidUntil < form.sentAt
        ? 'The quote cannot stop being valid before the day it was sent.'
        : '',
    quoteLink:
      !form.quoteLink.trim() || isHttpUrl(form.quoteLink.trim())
        ? ''
        : 'The link must be a web address starting http:// or https://.',
  }
  const invalid = Object.values(problems).some(Boolean)
  const shown = (field) => (attempted ? problems[field] : '')

  const confirm = () => {
    setAttempted(true)
    if (invalid) return
    run({
      quoteReference: form.quoteReference.trim(),
      quotePackage: form.quotePackage.trim(),
      quoteAllowance: allowance,
      quoteAmount: amount,
      ...(form.quoteCurrency.trim() ? { quoteCurrency: form.quoteCurrency.trim().toUpperCase() } : {}),
      quoteValidUntil: form.quoteValidUntil,
      ...(form.quoteLink.trim() ? { quoteLink: form.quoteLink.trim() } : {}),
      sentAt: form.sentAt,
      ...(form.note.trim() ? { note: form.note.trim() } : {}),
    })
  }

  const label = revised ? 'Record revised quote' : 'Record quote sent'

  return (
    <ActionDialog
      open={open}
      title={label}
      confirmLabel={label}
      maxWidth="md"
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={confirm}
    >
      <RecordSummary target={target} />
      {revised ? (
        <Alert severity="warning">
          This is a third or later occurrence, so the quote is recorded as a <b>revised quote</b>{' '}
          and the Enterprise+ upgrade stays required. Check the earlier quote still fits the
          customer&apos;s usage before reusing it.
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary">
          This records the Enterprise+ quotation you sent from your own mailbox and completes the
          quote action for this month. AmbuNet does not email the customer, change the contract or
          touch Stripe.
        </Typography>
      )}
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Quote reference"
            size="small"
            fullWidth
            required
            value={form.quoteReference}
            onChange={(event) => set('quoteReference', event.target.value)}
            error={!!shown('quoteReference')}
            helperText={shown('quoteReference')}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            label="Proposed Enterprise+ package"
            size="small"
            fullWidth
            required
            placeholder="e.g. Enterprise+ PTS 1,500"
            value={form.quotePackage}
            onChange={(event) => set('quotePackage', event.target.value)}
            error={!!shown('quotePackage')}
            helperText={shown('quotePackage')}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color={shown('quoteAllowance') ? 'error' : 'text.secondary'} display="block">
            {shown('quoteAllowance') ||
              'Proposed allowances - enter at least one (the totals the package would give).'}
          </Typography>
        </Grid>
        {QUOTE_METRICS.map((metric) => (
          <Grid item xs={12} md={4} key={metric}>
            <TextField
              label={METRIC_LABELS[metric]}
              size="small"
              fullWidth
              type="number"
              inputProps={{ min: 0, step: metric === 'controlRoomHoursPerMonth' ? 'any' : 1 }}
              value={form[metric]}
              onChange={(event) => set(metric, event.target.value)}
              error={!!shown('quoteAllowance')}
            />
          </Grid>
        ))}
        <Grid item xs={12} md={4}>
          <TextField
            label="Quoted fee"
            size="small"
            fullWidth
            required
            type="number"
            inputProps={{ min: 0, step: 'any' }}
            value={form.quoteAmount}
            onChange={(event) => set('quoteAmount', event.target.value)}
            error={!!shown('quoteAmount')}
            helperText={shown('quoteAmount')}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Currency"
            size="small"
            fullWidth
            value={form.quoteCurrency}
            onChange={(event) => set('quoteCurrency', event.target.value)}
            error={!!shown('quoteCurrency')}
            helperText={shown('quoteCurrency')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DateInput
            label="Valid until"
            required
            value={form.quoteValidUntil}
            onChange={(value) => set('quoteValidUntil', value)}
            error={shown('quoteValidUntil')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DateInput
            label="Date sent"
            required
            value={form.sentAt}
            onChange={(value) => set('sentAt', value)}
            min={earliest || undefined}
            max={todayInput()}
            error={shown('sentAt')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Link to the quote (optional)"
            size="small"
            fullWidth
            placeholder="https://…"
            value={form.quoteLink}
            onChange={(event) => set('quoteLink', event.target.value)}
            error={!!shown('quoteLink')}
            helperText={shown('quoteLink') || 'Where the quote document is kept, e.g. a SharePoint or Xero link.'}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Note (optional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.note}
            onChange={(event) => set('note', event.target.value)}
          />
        </Grid>
      </Grid>
      {onPreviewDraft && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<MailOutlineIcon />}
          onClick={() => onPreviewDraft(quotePreviewQuery(form))}
          disabled={busy}
          sx={{ alignSelf: 'flex-start' }}
        >
          Preview email draft with these details
        </Button>
      )}
    </ActionDialog>
  )
}

// Second or third occurrence: close the month's action without a quote or an
// upgrade. The month and its occurrence number stay on record.
export const SeasonalReviewDialog = ({ open, target, onClose, onSubmit }) => {
  const [rationale, setRationale] = useState('')
  const [attempted, setAttempted] = useState(false)
  const { busy, error, run } = useActionSubmit({ open, onSubmit, onClose })

  useEffect(() => {
    if (open) {
      setRationale('')
      setAttempted(false)
    }
  }, [open])

  const problem = reasonProblem(rationale, 'Write the rationale for the seasonal review')
  const confirm = () => {
    setAttempted(true)
    if (problem) return
    run({ rationale: rationale.trim() })
  }

  return (
    <ActionDialog
      open={open}
      title="Record seasonal review - no change"
      confirmLabel="Record seasonal review"
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={confirm}
    >
      <RecordSummary target={target} />
      <Alert severity="info">
        This closes the required action for {formatPeriod(target?.period, 'MMMM YYYY')} without a
        quote or an upgrade. The month and its occurrence number stay on record and the review
        sequence continues - the next exceeded month in the review period is counted after this
        one. Use Reset after correction instead only if the figures were wrong.
      </Alert>
      <TextField
        label="Rationale"
        size="small"
        fullWidth
        required
        multiline
        minRows={3}
        value={rationale}
        onChange={(event) => setRationale(event.target.value)}
        error={attempted && !!problem}
        helperText={
          (attempted && problem) ||
          'Why no change is needed, e.g. a seasonal peak in event cover. Recorded with your name and the time.'
        }
      />
      <Typography variant="caption" color="text.secondary">
        The customer is still told their usage: draft the seasonal review email afterwards and
        record it with Mark customer informed.
      </Typography>
    </ActionDialog>
  )
}

// After a seasonal review: record that the customer was told their usage.
export const CustomerInformedDialog = ({ open, target, onClose, onSubmit }) => {
  const [informedAt, setInformedAt] = useState(todayInput())
  const [attempted, setAttempted] = useState(false)
  const { busy, error, run } = useActionSubmit({ open, onSubmit, onClose })

  useEffect(() => {
    if (open) {
      setInformedAt(todayInput())
      setAttempted(false)
    }
  }, [open])

  const earliest = earliestDayFor(target)
  const problem = pastDateProblem(informedAt, earliest)
  const confirm = () => {
    setAttempted(true)
    if (problem) return
    run({ informedAt })
  }

  return (
    <ActionDialog
      open={open}
      title="Mark customer informed"
      confirmLabel="Mark customer informed"
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={confirm}
    >
      <RecordSummary target={target} />
      <Typography variant="body2" color="text.secondary">
        This records that you told the customer their usage for this month after the seasonal
        review - no change decision. AmbuNet does not send anything.
      </Typography>
      <DateInput
        label="Date the customer was told"
        required
        value={informedAt}
        onChange={setInformedAt}
        min={earliest || undefined}
        max={todayInput()}
        error={attempted && problem}
      />
    </ActionDialog>
  )
}

// "Reset after correction" - for data errors only. Either one month is voided
// (later months are renumbered) or every counted month in the active sequence
// is. Usage figures are never altered. `months` are exactly the periods whose
// record lists `reset` in its allowedActions (a month that counts in the
// sequence or has an open action); `sequencePeriods` is the active sequence;
// `confirmText` must be typed for the whole-sequence option.
export const ResetDialog = ({
  open,
  initialPeriod,
  months = [],
  sequencePeriods = [],
  confirmText,
  onClose,
  onSubmit,
}) => {
  const [scope, setScope] = useState('period')
  const [period, setPeriod] = useState('')
  const [reason, setReason] = useState('')
  const [typed, setTyped] = useState('')
  const [attempted, setAttempted] = useState(false)
  const { busy, error, run } = useActionSubmit({ open, onSubmit, onClose })

  useEffect(() => {
    if (!open) return
    const startWith = initialPeriod ?? (months.length === 1 ? months[0] : '')
    setScope(startWith || months.length > 0 || sequencePeriods.length === 0 ? 'period' : 'sequence')
    setPeriod(startWith)
    setReason('')
    setTyped('')
    setAttempted(false)
  }, [open])

  const wholeSequence = scope === 'sequence'
  const problems = {
    period: !wholeSequence && !period ? 'Choose the month to reset.' : '',
    sequence: wholeSequence && sequencePeriods.length === 0 ? 'There is no active sequence to reset.' : '',
    reason: reasonProblem(reason, 'Describe the data error being corrected'),
    typed: wholeSequence && typed !== confirmText ? `Type "${confirmText}" to confirm.` : '',
  }
  const invalid = Object.values(problems).some(Boolean)

  const confirm = () => {
    setAttempted(true)
    if (invalid) return
    run({ reason: reason.trim(), ...(wholeSequence ? {} : { period }) })
  }

  return (
    <ActionDialog
      open={open}
      title="Reset after correction"
      confirmLabel={wholeSequence ? 'Reset whole sequence' : 'Reset this month'}
      confirmColor="error"
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={confirm}
    >
      <Alert severity="error">
        For data errors only - for example journeys that were duplicates, or a control room left
        open by mistake. It is not a commercial decision: where the usage was real but no change
        is wanted, record a seasonal review - no change instead.
      </Alert>
      <Typography variant="body2" color="text.secondary">
        Nothing is deleted. A reset month keeps its usage figures and its whole history, is marked
        as voided and stops counting towards the review sequence.
      </Typography>
      <RadioGroup value={scope} onChange={(event) => setScope(event.target.value)}>
        <FormControlLabel
          value="period"
          control={<Radio size="small" />}
          componentsProps={{ typography: { variant: 'body2' } }}
          disabled={months.length === 0}
          label="This month only - the month is voided and later counted months are renumbered"
        />
        <FormControlLabel
          value="sequence"
          control={<Radio size="small" color="error" />}
          componentsProps={{ typography: { variant: 'body2' } }}
          disabled={sequencePeriods.length === 0}
          label="Whole active sequence - every counted month is voided and a fresh sequence starts"
        />
      </RadioGroup>
      {!wholeSequence && (
        <FormControl size="small" fullWidth error={attempted && !!problems.period}>
          <InputLabel>Month to reset</InputLabel>
          <Select value={period} label="Month to reset" onChange={(event) => setPeriod(event.target.value)}>
            {months.map((month) => (
              <MenuItem key={month} value={month}>
                {formatPeriod(month, 'MMMM YYYY')}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color={attempted && problems.period ? 'error' : 'text.secondary'} sx={{ mt: 0.5, mx: 1.75 }}>
            {(attempted && problems.period) ||
              (months.length === 0
                ? 'No month can be reset on its own at the moment. Months still open are recalculated every night, so a correction to the data takes effect without a reset.'
                : 'Only a finalised month that counts in the sequence, or has an open action, can be reset.')}
          </Typography>
        </FormControl>
      )}
      {wholeSequence && (
        <Alert severity="warning">
          {sequencePeriods.length > 0
            ? `Months that will no longer count: ${sequencePeriods.map((month) => formatPeriod(month)).join(', ')}. Every open action on them is closed as voided, and a fresh sequence starts from the month after the latest of them.`
            : 'There is no active sequence to reset.'}
        </Alert>
      )}
      <TextField
        label="Reason - what was wrong with the data"
        size="small"
        fullWidth
        required
        multiline
        minRows={3}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        error={attempted && !!problems.reason}
        helperText={(attempted && problems.reason) || 'Recorded with your name and the time on every month it affects.'}
      />
      {wholeSequence && (
        <TextField
          label={`Type "${confirmText}" to confirm`}
          size="small"
          fullWidth
          autoComplete="off"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          error={attempted && !!problems.typed}
          helperText={attempted && problems.typed}
        />
      )}
    </ActionDialog>
  )
}
